import { useEffect, useMemo, useState } from "react"
import { Box, ButtonBase, Drawer, Stack, Typography } from "@mui/material"
import { useSnackbar } from "notistack"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronDown, faChevronUp, faList, faPen, faPlus, faSun } from "@fortawesome/pro-light-svg-icons"
import { add, type Dinero, multiply } from "dinero.js"
import { makeStyles } from "tss-react/mui"

import { useMoney } from "../../../modules/currency/useMoney"
import { toDinero } from "../../../modules/currency/currencyLib"
import { useDateTime } from "../../../modules/localization/useDateTime"
import { useApiQuery } from "../../../core/api/useApiQuery"
import { useAppSelector } from "../../../modules/store/storeHooks"
import { selectEffectiveUserType } from "../../../modules/context/contextSelectors"
import { UserType } from "../../../modules/login/loginTypes"
import {
    ConfigQuery,
    fetchMeetingServiceConfiguration,
    MeetingServiceConfigurationResponse,
} from "../createOrder/createOrderApi"
import { DynamicConfigKey } from "../createOrder/components/form/formTypes"
import { useAppLocalization } from "../../../modules/localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"
import { TranslationKey } from "../../../modules/localization/translations"
import {
    fetchProductsForUser,
    FetchProductsForUserResponse,
    UserDetails,
} from "../../../modules/nonAdminUser/nonAdminUserApi"
import { MeetingServiceOrder } from "../../../modules/merchant/merchant/production/meetingService/meetingServiceTypes"
import {
    ProductionProductOption,
    SavedOrderInfo,
    useProductionOrderEdit,
} from "../../../modules/merchant/merchant/production/meetingService/productionView/useProductionOrderEdit"
import { mergeGroupsByTime } from "../../../modules/merchant/merchant/production/meetingService/productionView/productionDeliveryGroups"
import { ProductionOrderLineRow } from "../../../modules/merchant/merchant/production/meetingService/productionView/components/ProductionOrderLineRow"
import {
    ProductionAddLinePicker,
    ProductionMeetingDefaults,
} from "../../../modules/merchant/merchant/production/meetingService/productionView/components/ProductionAddLinePicker"
import { StandardButton } from "../buttons/StandardButton"
import { StandardLoadingButton } from "../buttons/LoadingButton"
import { OrderDrawerHeader } from "./OrderDrawerHeader"

const selectProductOptions = (data: FetchProductsForUserResponse): ProductionProductOption[] =>
    (data.productGroups ?? []).flatMap((group) =>
        (group.products ?? []).map((product) => ({
            productId: product.id,
            name: product.name,
            itemPrice: product.price,
            stock: product.stock,
            availability: product.orderDetails?.availability,
        })),
    )

const useStyles = makeStyles()((theme) => ({
    paper: {
        width: "max(48rem, 50vw)",
        maxWidth: "100%",
        display: "flex",
        flexDirection: "column",
    },
    tabs: {
        padding: "1rem 1rem 0",
        gap: ".5rem",
        flexWrap: "wrap",
        background: theme.palette.tailwind.slate[100],
    },
    tab: {
        "&.MuiButton-root": {
            minWidth: "7rem",
            alignItems: "flex-start",
            padding: ".5rem 1rem",
            borderRadius: ".75rem",
            border: `1px solid ${theme.palette.tailwind.slate[200]}`,
            background: theme.palette.background.paper,
            color: theme.palette.tailwind.slate[500],
            textTransform: "none",
        },
    },
    tabActive: {
        "&.MuiButton-root": {
            borderColor: theme.palette.tailwind.emerald[500],
            background: theme.palette.tailwind.emerald[50],
            color: theme.palette.tailwind.emerald[700],
        },
    },
    body: {
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        padding: "1rem",
        background: theme.palette.tailwind.slate[100],
    },
    sectionRow: {
        marginBottom: ".75rem",
    },
    sectionTitle: {
        color: theme.palette.tailwind.slate[500],
        textTransform: "uppercase",
    },
    linesCount: {
        color: theme.palette.tailwind.slate[400],
    },
    addButton: {
        "&.MuiButton-root": {
            marginTop: ".75rem",
            height: "3rem",
            borderRadius: ".75rem",
            border: `2px dashed ${theme.palette.tailwind.slate[300]}`,
            backgroundColor: "transparent",
            color: theme.palette.tailwind.emerald[600],
            fontWeight: 600,
            justifyContent: "flex-start",
        },
        "&.MuiButton-root:hover": {
            borderColor: theme.palette.tailwind.emerald[500],
            backgroundColor: theme.palette.tailwind.emerald[50],
        },
        "& .MuiButton-endIcon": {
            marginLeft: "auto",
            color: theme.palette.tailwind.slate[400],
        },
    },
    addPanel: {
        marginTop: ".5rem",
        borderRadius: ".75rem",
        overflow: "hidden",
        background: theme.palette.background.paper,
    },
    addOption: {
        display: "flex",
        width: "100%",
        justifyContent: "flex-start",
        textAlign: "left",
        alignItems: "center",
        gap: "1rem",
        padding: "1rem",
        "&:hover": { background: theme.palette.tailwind.slate[50] },
    },
    addOptionChip: {
        width: "2.5rem",
        height: "2.5rem",
        borderRadius: ".75rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: theme.palette.tailwind.emerald[50],
        color: theme.palette.tailwind.emerald[600],
        flexShrink: 0,
    },
    addOptionTitle: {
        color: theme.palette.tailwind.slate[900],
    },
    addOptionDesc: {
        color: theme.palette.tailwind.slate[500],
    },
    footer: {
        padding: "1rem",
        borderTop: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    totalLabel: {
        color: theme.palette.tailwind.slate[500],
    },
    totalValue: {
        color: theme.palette.tailwind.slate[900],
    },
    footerButtons: {
        flexGrow: 1,
    },
    saveButton: {
        "&.MuiButton-root": {
            backgroundColor: theme.palette.tailwind.teal[500],
            color: theme.palette.common.white,
        },
        "&.MuiButton-root:hover": {
            backgroundColor: theme.palette.tailwind.teal[600],
        },
        "&.MuiButton-root.Mui-disabled": {
            backgroundColor: theme.palette.tailwind.slate[200],
            color: theme.palette.tailwind.slate[400],
        },
    },
}))

type Props = Readonly<{
    open: boolean
    order: MeetingServiceOrder
    onClose: () => void
    onSaved?: (info: SavedOrderInfo) => void
}>

export function MeetingServiceQuickEditDrawer({ open, order, onClose, onSaved }: Props) {
    const { classes, cx } = useStyles()
    const moneyFactory = useMoney({})
    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()
    const { enqueueSnackbar } = useSnackbar()

    const edit = useProductionOrderEdit({
        order,
        onRequestDelete: onClose,
        onSaved: (info) => {
            enqueueSnackbar(l10n.getString("meeting-service-order-drawer-saved-snackbar"), { variant: "success" })
            onSaved?.(info)
            onClose()
        },
    })

    const groups = useMemo(() => mergeGroupsByTime(edit.deliveryGroups), [edit.deliveryGroups])
    const [activeKey, setActiveKey] = useState<string | null>(null)
    const activeGroup = groups.find((group) => group.deliveryKey === activeKey) ?? groups[0]

    // The quick-edit drawer is always in edit mode for the selected delivery tab.
    useEffect(() => {
        if (open && activeGroup && edit.editingKey !== activeGroup.deliveryKey && !edit.isSaving) {
            edit.startEdit(activeGroup.deliveryKey, activeGroup.deliveryTime)
        }
    }, [open, activeGroup?.deliveryKey])

    const [isAddPanelOpen, setIsAddPanelOpen] = useState(false)
    const [isServicePickerOpen, setIsServicePickerOpen] = useState(false)

    const productsQuery = useApiQuery(fetchProductsForUser, {
        queryName: UserDetails.USER_PRODUCTS_LIST,
        dependencies: {
            userId: edit.addLookup?.userId ?? 0,
            merchantId: edit.addLookup?.merchantId ?? 0,
            priceListId: edit.addLookup?.priceListId ?? 0,
            deliveryDate: edit.addLookup?.deliveryDate ?? "",
        },
        enabled: edit.isEditing && Boolean(edit.addLookup),
        select: selectProductOptions,
    })

    const deliveryDateTime = dateTimeFactory.fromApi(order.deliveryDetails.time)
    const meetingStart = dateTimeFactory.fromApi(order.meetingDetails?.startDateTime ?? "")
    const meetingEnd = dateTimeFactory.fromApi(order.meetingDetails?.endDateTime ?? "")
    const meetingDefaults: ProductionMeetingDefaults = {
        deliveryDate: deliveryDateTime,
        start: meetingStart.isValid ? meetingStart : deliveryDateTime.isValid ? deliveryDateTime : null,
        end: meetingEnd.isValid ? meetingEnd : null,
    }

    const isMerchantAdmin = useAppSelector(selectEffectiveUserType) === UserType.MERCHANT
    const configQuery = useApiQuery(fetchMeetingServiceConfiguration, {
        queryName: ConfigQuery.MEETING_SERVICE_CONFIG,
        dependencies: { userId: edit.addLookup?.userId ?? 0, merchantId: edit.addLookup?.merchantId ?? 0 },
        enabled: !isMerchantAdmin && Boolean(edit.addLookup),
        select: (data: MeetingServiceConfigurationResponse) => data.meetingServiceConfig,
    })
    const productWishActive = configQuery.data?.sections
        .flatMap(({ groups }) => groups.flatMap(({ fields }) => fields))
        .find((field) => field.key === DynamicConfigKey.PRODUCT_WISH)?.active
    const canAddProductWish = isMerchantAdmin || !!productWishActive

    const addOptionsAll: ReadonlyArray<{
        icon: typeof faList
        titleId: TranslationKey
        title: string
        descId: TranslationKey
        desc: string
        onClick: () => void
    }> = [
        {
            icon: faList,
            titleId: "meeting-service-order-drawer-add-catalog-title",
            title: "Item from catalogue",
            descId: "meeting-service-order-drawer-add-catalog-desc",
            desc: "Search the catalogue — price filled automatically",
            onClick: () => {
                edit.addBlankLine()
                setIsAddPanelOpen(false)
            },
        },
        {
            icon: faPen,
            titleId: "meeting-service-order-drawer-add-wish-title",
            title: "Free-text request",
            descId: "meeting-service-order-drawer-add-wish-desc",
            desc: "Free text — the chef reviews it",
            onClick: () => {
                edit.addBlankProductWish()
                setIsAddPanelOpen(false)
            },
        },
        {
            icon: faSun,
            titleId: "meeting-service-order-drawer-add-service-title",
            title: "Service",
            descId: "meeting-service-order-drawer-add-service-desc",
            desc: "Table setting, serving, clearing etc.",
            onClick: () => {
                setIsServicePickerOpen(true)
                setIsAddPanelOpen(false)
            },
        },
    ]

    const addOptions = addOptionsAll.filter(
        (option) => canAddProductWish || option.titleId !== "meeting-service-order-drawer-add-wish-title",
    )

    const orderTotal = groups
        .flatMap((group) => group.lines)
        .reduce<Dinero<number> | undefined>((total, line) => {
            const amount = multiply(toDinero(line.itemPrice), line.count)
            return total ? add(total, amount) : amount
        }, undefined)
    const totalLabel = orderTotal
        ? moneyFactory.format(orderTotal)
        : order.amount
          ? moneyFactory.format(toDinero(order.amount))
          : ""
    const activeLines = activeGroup?.lines ?? []

    return (
        <Drawer anchor="right" open={open} onClose={onClose} classes={{ paper: classes.paper }}>
            <OrderDrawerHeader
                title={order.meetingDetails?.meetingName}
                orderId={order.id}
                actions={[]}
                onClose={onClose}
            />

            <Stack direction="row" className={classes.tabs}>
                {groups.map((group, index) => {
                    const isActive = group.deliveryKey === activeGroup?.deliveryKey
                    const time = group.deliveryTime.isValid ? dateTimeFactory.formatTime(group.deliveryTime) : "-"
                    return (
                        <StandardButton
                            key={group.deliveryKey}
                            fullWidth={false}
                            variant="outlined"
                            className={cx(classes.tab, isActive && classes.tabActive)}
                            onClick={() => setActiveKey(group.deliveryKey)}
                        >
                            <Stack alignItems="flex-start">
                                <LocalizedStrict
                                    id="meeting-service-order-drawer-delivery-tab"
                                    vars={{ index: index + 1 }}
                                >
                                    <Typography
                                        component="span"
                                        variant="caption"
                                        fontWeight={700}
                                        letterSpacing=".05em"
                                    >
                                        {`DELIVERY ${index + 1}`}
                                    </Typography>
                                </LocalizedStrict>
                                <Typography component="span" variant="body1" fontWeight={700}>
                                    {time}
                                </Typography>
                            </Stack>
                        </StandardButton>
                    )
                })}
            </Stack>

            <Box className={classes.body}>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    className={classes.sectionRow}
                >
                    <LocalizedStrict id="meeting-service-order-drawer-order-lines-title">
                        <Typography
                            component="span"
                            variant="caption"
                            fontWeight={700}
                            letterSpacing=".05em"
                            className={classes.sectionTitle}
                        >
                            Order lines
                        </Typography>
                    </LocalizedStrict>
                    <LocalizedStrict
                        id="meeting-service-order-drawer-order-lines-count"
                        vars={{ count: activeLines.length }}
                    >
                        <Typography component="span" variant="body2" className={classes.linesCount}>
                            {`${activeLines.length} order lines`}
                        </Typography>
                    </LocalizedStrict>
                </Stack>

                <Stack spacing=".75rem">
                    {activeLines.map((line) => (
                        <ProductionOrderLineRow
                            key={line.key}
                            line={line}
                            isEditing
                            productOptions={productsQuery.data ?? []}
                            isProductsLoading={productsQuery.isInitialLoading}
                            isEditLoading={edit.isEditLoading}
                            isTouched={edit.touchedKeys.includes(line.key)}
                            usedProductIds={edit.usedProductIds}
                            onCountChange={edit.setLineCount}
                            onProductChange={edit.setLineProduct}
                            onWishChange={edit.setLineWish}
                            onRemove={edit.removeLine}
                        />
                    ))}
                </Stack>

                <LocalizedStrict id="meeting-service-production-view-add-menu-button">
                    <StandardButton
                        fullWidth
                        size="small"
                        variant="outlined"
                        className={classes.addButton}
                        startIcon={<FontAwesomeIcon icon={faPlus} />}
                        endIcon={<FontAwesomeIcon icon={isAddPanelOpen ? faChevronUp : faChevronDown} />}
                        disabled={!edit.addLookup}
                        onClick={() => setIsAddPanelOpen((prev) => !prev)}
                    >
                        Add line
                    </StandardButton>
                </LocalizedStrict>
                {isAddPanelOpen && (
                    <Box className={classes.addPanel}>
                        {addOptions.map((option) => (
                            <ButtonBase key={option.titleId} className={classes.addOption} onClick={option.onClick}>
                                <Box className={classes.addOptionChip}>
                                    <FontAwesomeIcon icon={option.icon} />
                                </Box>
                                <Box>
                                    <LocalizedStrict id={option.titleId}>
                                        <Typography component="div" fontWeight={700} className={classes.addOptionTitle}>
                                            {option.title}
                                        </Typography>
                                    </LocalizedStrict>
                                    <LocalizedStrict id={option.descId}>
                                        <Typography component="div" variant="caption" className={classes.addOptionDesc}>
                                            {option.desc}
                                        </Typography>
                                    </LocalizedStrict>
                                </Box>
                            </ButtonBase>
                        ))}
                    </Box>
                )}
                {isServicePickerOpen && edit.addLookup && (
                    <Box marginTop=".75rem">
                        <ProductionAddLinePicker
                            lookup={edit.addLookup}
                            seatCount={order.deliveryDetails.sittings}
                            usedProductIds={edit.usedProductIds}
                            meetingDefaults={meetingDefaults}
                            onAddExtraService={edit.addExtraService}
                            onClose={() => setIsServicePickerOpen(false)}
                        />
                    </Box>
                )}
            </Box>

            <Stack direction="row" alignItems="center" spacing="1.5rem" className={classes.footer}>
                <Stack>
                    <LocalizedStrict id="meeting-service-order-drawer-total-label">
                        <Typography
                            component="span"
                            variant="caption"
                            fontWeight={700}
                            letterSpacing=".05em"
                            className={classes.totalLabel}
                        >
                            TOTAL
                        </Typography>
                    </LocalizedStrict>
                    <Typography component="span" variant="h6" fontWeight={700} className={classes.totalValue}>
                        {totalLabel}
                    </Typography>
                </Stack>
                <Stack direction="row" spacing="1rem" className={classes.footerButtons}>
                    <LocalizedStrict id="meeting-service-production-view-cancel-button">
                        <StandardButton variant="outlined" color="grey" size="medium" onClick={onClose}>
                            Cancel
                        </StandardButton>
                    </LocalizedStrict>
                    <LocalizedStrict id="meeting-service-order-drawer-quick-edit-save-button">
                        <StandardLoadingButton
                            variant="contained"
                            color="primary"
                            size="medium"
                            className={classes.saveButton}
                            loading={edit.isSaving}
                            disabled={
                                !edit.hasChanges ||
                                (edit.saveAttempted && (edit.hasIncompleteLine || edit.hasOnlyExtraServices))
                            }
                            onClick={edit.save}
                        >
                            Save
                        </StandardLoadingButton>
                    </LocalizedStrict>
                </Stack>
            </Stack>
        </Drawer>
    )
}
